import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Button, Select, Space, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
// API
import { ADMIN_GET_CB_SUBSCRIPTION_PRODUCTS } from 'api/cbPromocodes/queries';
// Types
import {
  SubProductSubType,
  SubRecurringInterval,
  CbPromoCodeTargetInput,
} from 'api/graphql-global-types';
import { AdminGetCbSubscriptionProducts } from 'api/cbPromocodes/types/AdminGetCbSubscriptionProducts';
// Constants
import {
  SUB_PRODUCT_SUBTYPE_LABELS,
  SUB_RECURRING_INTERVAL_LABELS,
} from 'helpers/cbPromoCodes/constants';

const { Text } = Typography;
const { Option } = Select;

type TargetsSelectProps = {
  // Injected by antd Form.Item
  value?: CbPromoCodeTargetInput[];
  onChange?: (value: CbPromoCodeTargetInput[]) => void;
};

const TargetsSelect: React.FC<TargetsSelectProps> = ({
  value = [],
  onChange,
}) => {
  const { data, loading } = useQuery<AdminGetCbSubscriptionProducts>(
    ADMIN_GET_CB_SUBSCRIPTION_PRODUCTS,
    { fetchPolicy: 'cache-and-network' }
  );

  const products = useMemo(
    () => data?.adminGetCbSubscriptionProducts || [],
    [data]
  );

  // Distinct product subtypes available in the catalog, labelled by name.
  const productOptions = useMemo(() => {
    const bySubType = new Map<SubProductSubType, string>();
    products.forEach((product) => {
      if (!bySubType.has(product.subType)) {
        bySubType.set(
          product.subType,
          product.name || SUB_PRODUCT_SUBTYPE_LABELS[product.subType]
        );
      }
    });

    return Array.from(bySubType.entries()).map(([subType, name]) => ({
      subType,
      name,
    }));
  }, [products]);

  // Recurrences offered by the prices of a given product subtype.
  const recurrenceOptionsFor = (
    subType?: SubProductSubType
  ): SubRecurringInterval[] => {
    const intervals = new Set<SubRecurringInterval>();
    products
      .filter((product) => product.subType === subType)
      .forEach((product) =>
        product.prices.forEach((price) => {
          if (price.recurringInterval) {
            intervals.add(price.recurringInterval);
          }
        })
      );

    return Array.from(intervals);
  };

  const updateRow = (index: number, patch: Partial<CbPromoCodeTargetInput>) => {
    const next = value.map((row, i) =>
      i === index ? { ...row, ...patch } : row
    );
    onChange?.(next);
  };

  const addRow = () => {
    onChange?.([
      ...value,
      {
        product: productOptions[0]?.subType,
        recurrence: null,
      } as CbPromoCodeTargetInput,
    ]);
  };

  const removeRow = (index: number) => {
    onChange?.(value.filter((_, i) => i !== index));
  };

  return (
    <div>
      {!value.length && (
        <Text type="secondary">
          No targets — the code applies to every plan and addon.
        </Text>
      )}

      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {value.map((row, index) => (
          <Space key={index} align="start">
            <Select
              style={{ width: 160 }}
              placeholder="Product"
              loading={loading}
              value={row.product}
              onChange={(product: SubProductSubType) =>
                updateRow(index, { product, recurrence: null })
              }
            >
              {productOptions.map(({ subType, name }) => (
                <Option key={subType} value={subType}>
                  {name}
                </Option>
              ))}
            </Select>

            <Select
              style={{ width: 160 }}
              placeholder="Any recurrence"
              allowClear
              value={row.recurrence || undefined}
              onChange={(recurrence?: SubRecurringInterval) =>
                updateRow(index, { recurrence: recurrence || null })
              }
            >
              {recurrenceOptionsFor(row.product).map((interval) => (
                <Option key={interval} value={interval}>
                  {SUB_RECURRING_INTERVAL_LABELS[interval]}
                </Option>
              ))}
            </Select>

            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeRow(index)}
            />
          </Space>
        ))}
      </Space>

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addRow}
        disabled={!productOptions.length}
        style={{ marginTop: value.length ? 8 : 12 }}
      >
        Add target
      </Button>
    </div>
  );
};

export default TargetsSelect;
